"use client";

import { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Spinner,
  Card,
  Row,
  Col,
  Alert,
  Badge,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import api from "../../services/api";
import { toast } from "react-toastify";

const TEMPLATE_COLUMNS = [
  "userCode",
  "walletAmount",
  "creditAmount",
  "description",
];

const AdminRequestsImportComp = () => {
  const [stores, setStores] = useState([]);
  const [storeCode, setStoreCode] = useState("");

  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);

  const [loadingStores, setLoadingStores] = useState(true);
  const [readingFile, setReadingFile] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [result, setResult] = useState(null);
  const [globalError, setGlobalError] = useState("");

  // -----------------------------
  // دریافت لیست فروشگاه‌ها
  // -----------------------------
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data } = await api.get("/admin/stores");

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : [];

        setStores(list);
      } catch (err) {
        console.error(err);
        toast.error("خطا در دریافت لیست فروشگاه‌ها");
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, []);

  // -----------------------------
  // دانلود فایل نمونه
  // -----------------------------
  const downloadTemplate = () => {
    const sample = [
      {
        userCode: "U001",
        walletAmount: 300000,
        creditAmount: 200000,
        description: "خرید شهریور",
      },
      {
        userCode: "U002",
        walletAmount: 0,
        creditAmount: 500000,
        description: "خرید",
      },
      {
        userCode: "U003",
        walletAmount: 150000,
        creditAmount: 0,
        description: "خرید",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sample, {
      header: TEMPLATE_COLUMNS,
    });

    ws["!cols"] = [
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      { wch: 35 },
    ];

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Requests"
    );

    XLSX.writeFile(
      wb,
      "requests-template.xlsx"
    );
  };

  // -----------------------------
  // خواندن فایل Excel
  // -----------------------------
  const handleFile = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setGlobalError("");
    setResult(null);
    setRows([]);
    setFile(selectedFile);
    setReadingFile(true);

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = evt.target.result;

        const workbook = XLSX.read(data, {
          type: "array",
        });

        const firstSheetName =
          workbook.SheetNames[0];

        const sheet =
          workbook.Sheets[firstSheetName];

        const json = XLSX.utils.sheet_to_json(
          sheet,
          {
            defval: "",
          }
        );

        const mappedRows = json.map(
          (item, index) => {
            const walletAmount =
              Number(item.walletAmount || 0);

            const creditAmount =
              Number(item.creditAmount || 0);

            return {
              rowIndex: index + 2,

              userCode:
                item.userCode
                  ?.toString()
                  .trim() || "",

              walletAmount:
                Number.isFinite(walletAmount)
                  ? walletAmount
                  : 0,

              creditAmount:
                Number.isFinite(creditAmount)
                  ? creditAmount
                  : 0,

              description:
                item.description
                  ?.toString()
                  .trim() || "",

              valid:
                !!item.userCode &&
                (
                  walletAmount > 0 ||
                  creditAmount > 0
                ),

              error: "",
            };
          }
        );

        // اعتبارسنجی اولیه
        mappedRows.forEach((row) => {
          if (!row.userCode) {
            row.valid = false;
            row.error =
              "کد کاربر وارد نشده است.";
          } else if (
            row.walletAmount <= 0 &&
            row.creditAmount <= 0
          ) {
            row.valid = false;
            row.error =
              "حداقل یکی از مبالغ کیف پول یا اعتبار باید بیشتر از صفر باشد.";
          }
        });

        setRows(mappedRows);

        if (!mappedRows.length) {
          setGlobalError(
            "فایل اکسل فاقد ردیف قابل پردازش است."
          );
        }
      } catch (err) {
        console.error(err);

        setGlobalError(
          "خطا در خواندن فایل اکسل. فرمت فایل را بررسی کنید."
        );
      } finally {
        setReadingFile(false);
      }
    };

    reader.onerror = () => {
      setReadingFile(false);

      setGlobalError(
        "خطا در خواندن فایل."
      );
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  // -----------------------------
  // حذف فایل انتخاب‌شده
  // -----------------------------
  const clearFile = () => {
    setFile(null);
    setRows([]);
    setResult(null);
    setGlobalError("");

    const input =
      document.getElementById(
        "requests-excel-file"
      );

    if (input) {
      input.value = "";
    }
  };

  // -----------------------------
  // ارسال فایل
  // -----------------------------
  const handleUpload = async () => {
    if (!storeCode) {
      toast.error(
        "ابتدا فروشگاه را انتخاب کنید."
      );
      return;
    }

    if (!file) {
      toast.error(
        "ابتدا فایل اکسل را انتخاب کنید."
      );
      return;
    }

    if (!rows.length) {
      toast.error(
        "فایل اکسل فاقد اطلاعات است."
      );
      return;
    }

    const invalidRows =
      rows.filter((row) => !row.valid);

    if (invalidRows.length > 0) {
      toast.error(
        `تعداد ${invalidRows.length} ردیف دارای خطا است.`
      );
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();

      formData.append(
        "file",
        file
      );

      formData.append(
        "storeCode",
        storeCode
      );

      const { data } =
        await api.post(
          "/admin/requests/import-excel",
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

      setResult(data);

      toast.success(
        "فایل درخواست‌های خرید با موفقیت پردازش شد."
      );
    } catch (err) {
      console.error(err);

      const message =
        err?.response?.data?.message ||
        "خطا در ارسال فایل اکسل.";

      toast.error(
        Array.isArray(message)
          ? message.join("، ")
          : message
      );
    } finally {
      setUploading(false);
    }
  };

  const formatAmount = (value) => {
    return Number(
      value || 0
    ).toLocaleString("fa-IR");
  };

  const getStoreName = () => {
    const store = stores.find(
      (item) =>
        String(item.code) ===
        String(storeCode)
    );

    return store?.name || "";
  };

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <Card.Title className="mb-4">
            ورود درخواست‌های خرید توسط اکسل
          </Card.Title>

          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  فروشگاه
                </Form.Label>

                <Form.Select
                  value={storeCode}
                  onChange={(e) =>
                    setStoreCode(
                      e.target.value
                    )
                  }
                  disabled={
                    loadingStores ||
                    uploading
                  }
                >
                  <option value="">
                    انتخاب فروشگاه
                  </option>

                  {stores.map((store) => (
                    <option
                      key={
                        store.id ||
                        store.code
                      }
                      value={store.code}
                    >
                      {store.name}
                      {store.code
                        ? ` - ${store.code}`
                        : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={7}>
              <Form.Group>
                <Form.Label>
                  فایل درخواست‌ها
                </Form.Label>

                <Form.Control
                  id="requests-excel-file"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFile}
                  disabled={uploading}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex flex-wrap gap-2 mt-4">
            <Button
              variant="outline-success"
              onClick={downloadTemplate}
              disabled={uploading}
            >
              دانلود فایل نمونه
            </Button>

            {file && (
              <Button
                variant="outline-secondary"
                onClick={clearFile}
                disabled={uploading}
              >
                حذف فایل
              </Button>
            )}

            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={
                uploading ||
                readingFile ||
                !file ||
                !storeCode ||
                !rows.length
              }
            >
              {uploading ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  در حال ارسال...
                </>
              ) : (
                "ثبت درخواست‌ها"
              )}
            </Button>
          </div>

          {getStoreName() && (
            <Alert
              variant="info"
              className="mt-3 mb-0"
            >
              فروشگاه انتخاب‌شده:{" "}
              <strong>
                {getStoreName()}
              </strong>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {globalError && (
        <Alert variant="danger">
          {globalError}
        </Alert>
      )}

      {readingFile && (
        <div className="text-center my-4">
          <Spinner animation="border" />
          <div className="mt-2">
            در حال خواندن فایل...
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Card.Title className="mb-0">
                پیش‌نمایش فایل
              </Card.Title>

              <Badge bg="primary">
                {rows.length} ردیف
              </Badge>
            </div>

            <div className="table-responsive">
              <Table
                striped
                bordered
                hover
                responsive
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>کد کاربر</th>
                    <th>کیف پول</th>
                    <th>اعتبار</th>
                    <th>توضیحات</th>
                    <th>وضعیت</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.rowIndex}
                    >
                      <td>
                        {row.rowIndex}
                      </td>

                      <td>
                        {row.userCode ||
                          "-"}
                      </td>

                      <td>
                        {formatAmount(
                          row.walletAmount
                        )}
                      </td>

                      <td>
                        {formatAmount(
                          row.creditAmount
                        )}
                      </td>

                      <td>
                        {row.description ||
                          "-"}
                      </td>

                      <td>
                        {row.valid ? (
                          <Badge bg="success">
                            آماده ثبت
                          </Badge>
                        ) : (
                          <div>
                            <Badge bg="danger">
                              خطا
                            </Badge>

                            <div className="small text-danger mt-1">
                              {row.error}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {result && (
        <Card>
          <Card.Body>
            <Card.Title className="mb-4">
              نتیجه پردازش
            </Card.Title>

            <Row className="g-3 mb-4">
              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">
                      کل ردیف‌ها
                    </div>
                    <h4>
                      {result.total ?? 0}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">
                      موفق
                    </div>
                    <h4 className="text-success">
                      {result.successCount ??
                        0}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">
                      رد شده
                    </div>
                    <h4 className="text-warning">
                      {result.skippedCount ??
                        0}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={3}>
                <Card className="h-100">
                  <Card.Body>
                    <div className="text-muted">
                      خطا
                    </div>
                    <h4 className="text-danger">
                      {result.errorCount ??
                        0}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {Array.isArray(
              result.results
            ) &&
              result.results.length > 0 && (
                <Table
                  striped
                  bordered
                  hover
                  responsive
                >
                  <thead>
                    <tr>
                      <th>ردیف Excel</th>
                      <th>وضعیت</th>
                      <th>شناسه درخواست</th>
                      <th>شناسه کاربر</th>
                      <th>پیام</th>
                    </tr>
                  </thead>

                  <tbody>
                    {result.results.map(
                      (item, index) => (
                        <tr
                          key={
                            `${item.rowIndex}-${index}`
                          }
                        >
                          <td>
                            {item.rowIndex}
                          </td>

                          <td>
                            {item.status ===
                            "success" ? (
                              <Badge bg="success">
                                موفق
                              </Badge>
                            ) : item.status ===
                              "skipped" ? (
                              <Badge bg="warning">
                                رد شده
                              </Badge>
                            ) : (
                              <Badge bg="danger">
                                خطا
                              </Badge>
                            )}
                          </td>

                          <td>
                            {item.requestId ??
                              "-"}
                          </td>

                          <td>
                            {item.userId ??
                              "-"}
                          </td>

                          <td>
                            {item.message ||
                              "-"}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </Table>
              )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default AdminRequestsImportComp;
